import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Map, Marker, InfoWindow, MapCameraChangedEvent } from '@vis.gl/react-google-maps';
import { Card } from '~/components/ui/card';
import { type WorkspaceSearchItem } from '~/.server/api/routers/workspace';
import { getImageUrl } from '~/utils/misc';
import { Link, useSearchParams } from '@remix-run/react';
import { urlPattern } from '~/utils/urlPattern';
import { MapBounds, deserializeBounds, haveBoundsChangedSignificantly, serializeBounds } from '~/utils/mapCache';
import { GoogleMapsProvider } from './googleMapsProvider';

const API_KEY = 'AIzaSyDOMCcEqcpRrRSLc4jfJdKFcgYz60ghq6c'; // Replace with your actual API key

type Coordinates = {
  latitude?: string | null;
  longitude?: string | null;
};

const COORDINATE_REGEX = /^-?\d+(\.\d+)?$/;

export const isValidCoordinates = (location: Partial<Coordinates>): location is { latitude: string; longitude: string } => {
  if (!location.latitude || !location.longitude) return false;

  // Ensure the strings are valid number formats (no letters or invalid characters)
  if (!COORDINATE_REGEX.test(location.latitude) || !COORDINATE_REGEX.test(location.longitude)) {
    return false;
  }

  const lat = parseFloat(location.latitude);
  const lng = parseFloat(location.longitude);

  return (
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180
  );
};

export const coordinatesUtils = {
  isValid: isValidCoordinates,
  parse: (location: { latitude: string; longitude: string }) => ({
    lat: parseFloat(location.latitude),
    lng: parseFloat(location.longitude)
  }),
};

type WorkspaceMapProps = {
  workspaces: WorkspaceSearchItem[];
  height?: string;
  className?: string;
  zoom?: number;
  searchWithinBounds?: boolean;
  onBoundsChange?: (bounds: MapBounds) => void;
};

const WorkspaceMap = ({
  workspaces,
  height = 'h-96',
  className = '',
  zoom = 12,
  searchWithinBounds = false,
  onBoundsChange
}: WorkspaceMapProps) => {
  const [isClient, setIsClient] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceSearchItem | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const boundsParam = searchParams.get('bounds');
  
  // Use useRef instead of useState to ensure the value persists across renders
  const lastValidCenterRef = useRef<{ lat: number, lng: number } | null>(null);
  
  // Initialize from localStorage if available - run only once on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const savedCenter = localStorage.getItem('lastMapCenter');
        if (savedCenter) {
          const parsed = JSON.parse(savedCenter);
          lastValidCenterRef.current = parsed;
        }
      } catch (e) {
        console.error('Error loading from localStorage:', e);
      }
    }
  }, []);
  
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Save to localStorage when center changes
  const saveCenter = useCallback((center: { lat: number, lng: number }) => {
    if (center.lat !== 0 && center.lng !== 0) {
      lastValidCenterRef.current = center;
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('lastMapCenter', JSON.stringify(center));
        } catch (e) {
          console.error('Error saving to localStorage:', e);
        }
      }
    }
  }, []);

  // Handle map bounds change
  const handleBoundsChanged = useCallback((ev: MapCameraChangedEvent) => {
    // Always update the last valid center when the map is moved
    if (ev.detail.center) {
      const newCenter = {
        lat: ev.detail.center.lat,
        lng: ev.detail.center.lng
      };
      saveCenter(newCenter);
    }
    
    if (!searchWithinBounds) return;
    
    // Get the current map bounds
    const bounds = ev.detail.bounds;
    if (!bounds) return;
    
    const newBounds: MapBounds = {
      north: bounds.north,
      south: bounds.south,
      east: bounds.east,
      west: bounds.west
    };
    
    
    const currentBounds = deserializeBounds(boundsParam);
    
    // Only update if bounds have changed significantly
    if (haveBoundsChangedSignificantly(currentBounds, newBounds)) {
      // Update URL params
      const newParams = new URLSearchParams(searchParams);
      newParams.set('bounds', serializeBounds(newBounds));
      setSearchParams(newParams, { replace: true });
      
      // Call the callback if provided
      if (onBoundsChange) {
        onBoundsChange(newBounds);
      }
    }
  }, [boundsParam, searchParams, setSearchParams, onBoundsChange, searchWithinBounds]);

  // Calculate map center and bounds
  const { center } = useMemo(() => {
    const validWorkspaces = workspaces.filter(coordinatesUtils.isValid);

    if (validWorkspaces.length === 0) {
      // Use last valid center if available from ref, otherwise default to Madrid
      const defaultCenter = lastValidCenterRef.current || { lat: 40.416775, lng: -3.703790 }; // Default to Madrid
      
      return {
        center: defaultCenter,
        bounds: undefined
      };
    }

    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    let sumLat = 0;
    let sumLng = 0;

    validWorkspaces.forEach(workspace => {
      const lat = parseFloat(workspace.latitude!);
      const lng = parseFloat(workspace.longitude!);
      
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      
      sumLat += lat;
      sumLng += lng;
    });

    // Calculate new center
    const newCenter = {
      lat: sumLat / validWorkspaces.length,
      lng: sumLng / validWorkspaces.length
    };
    
    return {
      center: newCenter,
      bounds: {
        north: maxLat,
        south: minLat,
        east: maxLng,
        west: minLng
      }
    };
  }, [workspaces]);

  // Save the calculated center when workspaces change
  useEffect(() => {
    if (center.lat !== 0 && center.lng !== 0) {
      saveCenter(center);
    }
  }, [center, saveCenter]);

  // NOTE: don't render on SSR
  if (!isClient) return <div className={`${className} ${height}`}></div>; // Empty placeholder with same dimensions
  
  const validWorkspaces = workspaces.filter(coordinatesUtils.isValid);

  return (
    <Card className={`${className} overflow-hidden`}>
      <div className={`w-full ${height}`}>
        <GoogleMapsProvider>
          <Map
            defaultCenter={center}
            defaultZoom={zoom}
            gestureHandling="greedy"
            mapId="workspaces-map"
            disableDefaultUI={false}
            zoomControl={true}
            clickableIcons={false}
            onCameraChanged={handleBoundsChanged}
            styles={[
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]}
          >
            {validWorkspaces.map((workspace) => (
              <Marker
                key={workspace.id}
                position={{
                  lat: parseFloat(workspace.latitude!),
                  lng: parseFloat(workspace.longitude!)
                }}
                onClick={() => setSelectedWorkspace(workspace)}
              />
            ))}

            {selectedWorkspace && coordinatesUtils.isValid(selectedWorkspace) && (
              <InfoWindow
                position={{
                  lat: parseFloat(selectedWorkspace.latitude!),
                  lng: parseFloat(selectedWorkspace.longitude!)
                }}
                onCloseClick={() => setSelectedWorkspace(null)}
              >
              <Link
                to={urlPattern.build({
                  category: selectedWorkspace.category,
                  city: selectedWorkspace.citySlug,
                  space: selectedWorkspace.slug,
                })}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="max-w-xs p-2">
                  {selectedWorkspace.image && (
                    <div className="relative w-full h-48 mb-2">
                      <img
                        src={getImageUrl(selectedWorkspace.image.slug, '300x200')}
                        alt={selectedWorkspace.image.altText || selectedWorkspace.title}
                        className="w-full h-full object-cover rounded"
                        style={selectedWorkspace.image?.transforms?.rotation ? { transform: `rotate(${selectedWorkspace.image.transforms.rotation}deg)` } : undefined}
                      />
                    </div>
                  )}
                  <h3 className="font-medium text-sm mb-1 line-clamp-2">
                    {selectedWorkspace.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {selectedWorkspace.city}, {selectedWorkspace.countryCode}
                  </p>
                  {selectedWorkspace.priceMin && (
                    <p className="text-sm font-medium mt-1">
                      {`${selectedWorkspace.priceMin}€`}
                    </p>
                  )}
                </div>
                </Link>
              </InfoWindow>
            )}
          </Map>
        </GoogleMapsProvider>
      </div>
    </Card>
  );
};

export default WorkspaceMap;
